import defLocale from "./locales/en";

declare let dhx: any;

export type IStrToDate = (date: string) => Date;
export type IDateToStr = (date: Date) => string;
export type IDateAdd = (date: Date, inc: number, mode: string) => Date;

export class DateFormatter {
	public locale: any;
	public startOnMonday: boolean;
	private _len: any;
	private custom: { [name: string]: IDateAdd } = {}; // hash of custom data adding modes
	constructor() {
		this.locale = {};
		try {
			if (dhx.i18n.pivot.date) {
				this.locale.date = dhx.i18n.pivot.date;
			} else {
				throw new Error();
			}
		} catch (err) {
			this.locale.date = defLocale.date;
		}
		this.startOnMonday = true; // FIXME
		this._len = {
			year: 60 * 60 * 24 * 364.25,
			month: 60 * 60 * 24 * 30.5,
			day: 60 * 60 * 24,
			hour: 60 * 60,
			minute: 60,
			second: 1,
		};
	}

	date_part(date: Date): Date {
		const old = new Date(date);
		date.setHours(0);
		date.setMinutes(0);
		date.setSeconds(0);
		date.setMilliseconds(0);
		if (
			date.getHours() && // shift to yesterday on dst
			(date.getDate() < old.getDate() ||
				date.getMonth() < old.getMonth() ||
				date.getFullYear() < old.getFullYear())
		) {
			date.setTime(date.getTime() + 60 * 60 * 1000 * (24 - date.getHours()));
		}
		return date;
	}

	time_part(date: Date): number {
		return (date.valueOf() / 1000 - date.getTimezoneOffset() * 60) % 86400;
	}

	week_start(date: Date): Date {
		let shift = date.getDay();
		if (this.startOnMonday) {
			if (shift === 0) {
				shift = 6;
			} else {
				shift--;
			}
		}
		return this.date_part(this.add(date, -1 * shift, "day"));
	}

	month_start(date: Date): Date {
		return new Date(date.getFullYear(), date.getMonth(), 1);
	}

	month_end(date: Date): Date {
		return new Date(date.getFullYear(), date.getMonth() + 1, 0);
	}

	year_start(date: Date): Date {
		date.setMonth(0);
		return this.month_start(date);
	}

	day_start(date: Date): Date {
		return this.date_part(date);
	}

	length(a: Date, b: Date, unit: string) {
		unit = unit || "day";
		return Math.abs(Math.round((a.valueOf() - b.valueOf()) / (1000 * this._len[unit])));
	}

	_add_days(date: Date, inc: number): Date {
		const ndate = new Date(date.valueOf());

		ndate.setDate(ndate.getDate() + inc);
		if (
			inc >= 0 &&
			!date.getHours() &&
			ndate.getHours() && // shift to yesterday on dst
			(ndate.getDate() < date.getDate() ||
				ndate.getMonth() < date.getMonth() ||
				ndate.getFullYear() < date.getFullYear())
		) {
			ndate.setTime(ndate.getTime() + 60 * 60 * 1000 * (24 - ndate.getHours()));
		}

		return ndate;
	}

	registerCustomMode(name: string, handler: IDateAdd) {
		this.custom[name] = handler;
	}

	add(date: Date, inc: number, mode: string): Date {
		let ndate = new Date(date.valueOf());
		switch (mode) {
			case "day":
				ndate = this._add_days(ndate, inc);
				break;
			case "week":
				ndate = this._add_days(ndate, inc * 7);
				break;
			case "month":
				ndate.setMonth(ndate.getMonth() + inc);
				break;
			case "year":
				ndate.setFullYear(ndate.getFullYear() + inc);
				break;
			case "hour":
				/*
				 setHour(getHour() + inc) and setMinutes gives weird result when is applied on a Daylight Saving time switch
				 setTime seems working as expected
				*/
				ndate.setTime(ndate.getTime() + inc * 60 * 60 * 1000);
				break;
			case "minute":
				ndate.setTime(ndate.getTime() + inc * 60 * 1000);
				break;
			default:
				return this.custom[mode](date, inc, mode);
		}
		return ndate;
	}

	to_fixed(num: number): string {
		if (num < 10) {
			return "0" + num;
		}
		return "" + num;
	}

	copy(date: Date): Date {
		return new Date(date.valueOf());
	}

	date_to_str(format: string, utc?: boolean): IDateToStr {
		format = format.replace(/%[a-zA-Z]/g, a => {
			switch (a) {
				case "%d":
					return '"+to_fixed(date.getDate())+"';
				case "%m":
					return '"+to_fixed((date.getMonth()+1))+"';
				case "%j":
					return '"+date.getDate()+"';
				case "%n":
					return '"+(date.getMonth()+1)+"';
				case "%y":
					return '"+to_fixed(date.getFullYear()%100)+"';
				case "%Y":
					return '"+date.getFullYear()+"';
				case "%D":
					return '"+locale.date.dayShort[date.getDay()]+"';
				case "%l":
					return '"+locale.date.dayFull[date.getDay()]+"';
				case "%P":
					return '"+date.getDay()+"';
				case "%M":
					return '"+locale.date.monthShort[date.getMonth()]+"';
				case "%F":
					return '"+locale.date.monthFull[date.getMonth()]+"';
				case "%h":
					return '"+to_fixed((date.getHours()+11)%12+1)+"';
				case "%g":
					return '"+((date.getHours()+11)%12+1)+"';
				case "%G":
					return '"+date.getHours()+"';
				case "%H":
					return '"+to_fixed(date.getHours())+"';
				case "%i":
					return '"+to_fixed(date.getMinutes())+"';
				case "%a":
					return '"+(date.getHours()>11?"pm":"am")+"';
				case "%A":
					return '"+(date.getHours()>11?"PM":"AM")+"';
				case "%s":
					return '"+to_fixed(date.getSeconds())+"';
				case "%W":
					return '"+to_fixed(getISOWeek(date))+"';
				case "%Q":
					return '"+locale.date.quarter[getQuarter(date)-1]+"';
				case "%q":
					return '"+getQuarter(date)+"';
				default:
					return a;
			}
		});
		if (utc) {
			format = format.replace(/date\.get/g, "date.getUTC");
		}
		const code = new Function(
			"date",
			"to_fixed",
			"locale",
			"getISOWeek",
			"getQuarter",
			' return "' + format + '";'
		);
		return (date: Date): string => {
			// eslint-disable-next-line @typescript-eslint/unbound-method
			return code(date, this.to_fixed, defLocale, this.getISOWeek, this.getQuarter);
		};
	}

	str_to_date(format: string, utc?: boolean): IStrToDate {
		let splt = "if (typeof date === 'object') return date; var temp=date.match(/[a-zA-Z]+|[0-9]+/g);";
		const mask = format.match(/%[a-zA-Z]/g);
		for (let i = 0; i < mask.length; i++) {
			switch (mask[i]) {
				case "%j":
				case "%d":
					splt += "set[2]=temp[" + i + "]||1;";
					break;
				case "%n":
				case "%m":
					splt += "set[1]=(temp[" + i + "]||1)-1;";
					break;
				case "%y":
					splt += "set[0]=temp[" + i + "]*1+(temp[" + i + "]>50?1900:2000);";
					break;
				case "%g":
				case "%G":
				case "%h":
				case "%H":
					splt += "set[3]=temp[" + i + "]||0;";
					break;
				case "%i":
					splt += "set[4]=temp[" + i + "]||0;";
					break;
				case "%Y":
					splt += "set[0]=temp[" + i + "]||0;";
					break;
				case "%a":
				case "%A":
					splt += "set[3]=set[3]%12+((temp[" + i + "]||'').toLowerCase()=='am'?0:12);";
					break;
				case "%s":
					splt += "set[5]=temp[" + i + "]||0;";
					break;
				case "%M":
					splt += "set[1]=locale.date.month_short_hash[temp[" + i + "]]||0;";
					break;
				case "%F":
					splt += "set[1]=locale.date.month_full_hash[temp[" + i + "]]||0;";
					break;
				default:
					break;
			}
		}
		let params = "set[0],set[1],set[2],set[3],set[4],set[5]";
		if (utc) {
			params = " Date.UTC(" + params + ")";
		}

		const code = new Function(
			"to_fixed",
			"locale",
			"date",
			"var set=[0,0,1,0,0,0]; " + splt + " return new Date(" + params + ");"
		);
		return (date: string): Date => {
			// eslint-disable-next-line @typescript-eslint/unbound-method
			return code(this.to_fixed, this.locale, date);
		};
	}

	getISOWeek(ndate: Date): number {
		if (!ndate) {
			return 0;
		}

		let nday = ndate.getDay();
		if (nday === 0) {
			nday = 7;
		}
		const firstThursday = new Date(ndate.valueOf());
		firstThursday.setDate(ndate.getDate() + (4 - nday));
		const yearNumber = firstThursday.getFullYear(); // year of the first Thursday
		const ordinalDate = Math.round(
			(firstThursday.getTime() - new Date(yearNumber, 0, 1).getTime()) / 86400000
		); // ordinal date of the first Thursday - 1 (so not really ordinal date)
		const weekNumber = 1 + Math.floor(ordinalDate / 7);
		return weekNumber;
	}

	getUTCISOWeek(ndate: Date): number {
		return this.getISOWeek(this.convert_to_utc(ndate));
	}

	convert_to_utc(date: Date): Date {
		return new Date(
			date.getUTCFullYear(),
			date.getUTCMonth(),
			date.getUTCDate(),
			date.getUTCHours(),
			date.getUTCMinutes(),
			date.getUTCSeconds()
		);
	}

	getQuarter(d: Date): number {
		d = d || new Date();
		return Math.floor(d.getMonth() / 3) + 1;
	}
}
